import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Heart,
  MessageCircle,
  Share2,
  CheckCircle2,
  MoreHorizontal,
  Play,
  Volume2,
  VolumeX,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Sparkles,
  Lock,
} from "lucide-react";
import styles from "./FeedPostCard.module.css";
import { FeedPost } from "./dummyFeedData";
import { showToast } from "../../../utils/toast";

interface FeedPostCardProps {
  post: FeedPost;
}

export default function FeedPostCard({ post }: FeedPostCardProps) {
  const history = useHistory();

  const [isLiked, setIsLiked] = useState(Boolean(post.isLiked));
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const mediaList = post.media && post.media.length > 0 ? post.media : [];
  const currentMedia = mediaList[currentMediaIndex];
  const isPostLocked = currentMedia?.isLocked && !isUnlocked;
  const isVideo = currentMedia?.type === "video";

  const handleLike = () => {
    setIsLiked((prev) => !prev);
    showToast(isLiked ? "Post unliked" : "Liked post!");
  };

  const handleTip = () => {
    showToast(`Sent Tip to ${post.creator.name}!`);
  };

  const handleMessage = () => {
    history.push(`/messages?user=${post.creator.username}`);
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}/${post.creator.username}`);
      showToast("Post link copied to clipboard!");
    } else {
      showToast("Link ready to share!");
    }
  };

  const handleUnlock = () => {
    setIsUnlocked(true);
    showToast(`Unlocked exclusive content from ${post.creator.name}!`);
  };

  const prevMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentMediaIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1));
  };

  const nextMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentMediaIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0));
  };

  return (
    <article className={styles.postCard}>
      {/* 1. Header */}
      <div className={styles.postHeader}>
        <div
          className={styles.authorRow}
          onClick={() => history.push(`/${post.creator.username}`)}
        >
          <img
            src={post.creator.avatar}
            alt={post.creator.name}
            className={styles.authorAvatar}
          />
          <div className={styles.authorMeta}>
            <div className={styles.authorNameRow}>
              <span className={styles.authorName}>{post.creator.name}</span>
              {post.creator.isVerified && (
                <CheckCircle2 size={14} className={styles.verifiedBadge} />
              )}
            </div>
            <span className={styles.authorHandle}>
              @{post.creator.username} • {post.timeAgo || post.createdAt || "2h ago"}
            </span>
          </div>
        </div>

        <button className={styles.moreBtn} aria-label="More options">
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* 2. Media Viewport */}
      {currentMedia && (
        <div className={styles.mediaContainer}>
          <img
            src={currentMedia.url}
            alt="Feed post visual"
            className={styles.mediaImg}
          />

          {/* Carousel Arrows (if multi-media or video) */}
          {mediaList.length > 1 && (
            <>
              <button
                className={`${styles.carouselNav} ${styles.carouselNavLeft}`}
                onClick={prevMedia}
                aria-label="Previous media"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                className={`${styles.carouselNav} ${styles.carouselNavRight}`}
                onClick={nextMedia}
                aria-label="Next media"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {/* Video Overlay: Center Play Button */}
          {isVideo && (
            <button
              className={styles.playCenterBtn}
              onClick={() => setIsPlaying((prev) => !prev)}
              aria-label={isPlaying ? "Pause video" : "Play video"}
            >
              <Play size={24} fill="#ffffff" color="#ffffff" />
            </button>
          )}

          {/* Video Audio Control */}
          {isVideo && (
            <button
              className={styles.volumeBtn}
              onClick={() => setIsMuted((prev) => !prev)}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          )}

          {/* Carousel Indicator Dots */}
          {isVideo && (
            <div className={styles.paginationDots}>
              <span className={`${styles.dot} ${styles.dotActive}`} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          )}

          {/* Locked VIP Content Overlay */}
          {isPostLocked && (
            <div className={styles.lockedOverlay}>
              <div className={styles.lockIconCircle}>
                <Lock size={26} />
              </div>
              <h3 className={styles.lockedTitle}>Exclusive Content</h3>
              <p className={styles.lockedDesc}>
                Unlock full 4K ultra-high resolution post from {post.creator.name}.
              </p>
              <button className={styles.unlockBtn} onClick={handleUnlock}>
                <Sparkles size={16} />
                <span>Unlock for {currentMedia.unlockPrice || 50} Credits</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* 3. Caption & Hashtags */}
      <div className={styles.captionSection}>
        <p className={styles.captionText}>{post.caption}</p>
        {post.hashtags && post.hashtags.length > 0 && (
          <div className={styles.tagsRow}>
            {post.hashtags.map((tag) => (
              <span key={tag} className={styles.tagItem}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 4. Action Bar matching Figma */}
      <div className={styles.actionsBar}>
        <div className={styles.leftActions}>
          <button
            className={`${styles.actionButton} ${
              isLiked ? styles.actionButtonLiked : ""
            }`}
            onClick={handleLike}
          >
            <Heart size={18} fill={isLiked ? "#fe5a97" : "none"} />
            <span>{post.likesCount}</span>
          </button>

          <button className={styles.actionButton}>
            <MessageCircle size={18} />
            <span>{post.commentsCount}</span>
          </button>

          <button className={styles.actionButton} onClick={handleShare}>
            <Share2 size={18} />
            <span>{post.sharesCount}</span>
          </button>
        </div>

        <div className={styles.rightActions}>
          <button
            className={styles.tipCircleBtn}
            onClick={handleTip}
            title="Tip Creator"
          >
            <span>T</span>
          </button>

          <button className={styles.messageBtn} onClick={handleMessage}>
            <MessageSquare size={15} />
            <span>Message</span>
          </button>
        </div>
      </div>
    </article>
  );
}
